import dayjs from 'dayjs';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { Config, Routes } from 'config';
import { AppDispatch } from 'store/store';
import { setLoading } from 'store/slices/loader';
import { logout, updateAuthUser } from 'store/slices/auth';
import {
  getToken,
  saveTokens,
  getTenantId,
  getAuthStage,
  saveAuthStage,
  parseAxiosError,
} from 'utils';

import { URL } from './constants';

const { baseURL } = Config;

export const apiInstance = axios.create({ baseURL });

export const apiLoaderInstance = axios.create({ baseURL });

const skipRefreshUrls = [URL.LOGIN, URL.LOGOUT, URL.SIGNUP, URL.REFRESH];

const logoutOn401Urls = [URL.MFA_ENROLL, URL.MFA_VERIFY, URL.MFA_CHALLENGE];

const maxRequestRetryAttempts = 1;

const wafIntegrationPollDelayMs = 100;
const wafIntegrationMaxAttempts = 10;
const wafTokenHeaderKey = 'x-aws-waf-token';
const sessionIdStorageKey = 'session_id';

type RetriableAxiosConfig = InternalAxiosRequestConfig & {
  _retryCount?: number;
};

const resolveWafIntegration = async () => {
  for (let attempt = 0; attempt < wafIntegrationMaxAttempts; attempt += 1) {
    const wafIntegration =
      typeof window !== 'undefined' ? window.AwsWafIntegration : undefined;

    if (wafIntegration?.getToken) {
      return wafIntegration;
    }

    if (attempt < wafIntegrationMaxAttempts - 1) {
      await new Promise<void>((resolve) =>
        setTimeout(resolve, wafIntegrationPollDelayMs)
      );
    }
  }

  return undefined;
};

const tryAttachWafToken = async (headers: AxiosHeaders) => {
  if (!Config.waf.enabled || headers.has(wafTokenHeaderKey)) {
    return;
  }

  const wafIntegration = await resolveWafIntegration();

  if (!wafIntegration?.getToken) {
    return;
  }

  try {
    const wafToken = await wafIntegration.getToken();

    if (wafToken) {
      headers.set(wafTokenHeaderKey, wafToken);
    }
  } catch (e) {
    console.error('cannot get waf token', e);
  }
};

const configRequest = async (axiosConfig: InternalAxiosRequestConfig) => {
  const isRefresh = axiosConfig.url === URL.REFRESH;
  const token = getToken(isRefresh ? 'refreshToken' : 'accessToken');
  const config = { ...axiosConfig };
  const headers = ensureAxiosHeaders(config.headers);
  const tenantId = getTenantId();

  if (
    tenantId &&
    config.url !== URL.LOGIN &&
    config.url !== URL.PUBLIC_TENANTS
  ) {
    headers.set('x-tenant-id', tenantId);
  }

  if (
    Config.projectId &&
    (config.url === URL.LOGIN ||
      config.url === URL.FORGOT_PASSWORD ||
      config.url === URL.PUBLIC_TENANTS ||
      config.url === URL.PUBLIC_SIGNUP_CONFIG)
  ) {
    headers.set('x-project-id', Config.projectId);
  }

  headers.set('x-session-id', getSessionId());

  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  await tryAttachWafToken(headers);

  config.headers = headers;

  return config;
};

let refreshPromise: Promise<string> | null = null;
let captchaPromise: Promise<string> | null = null;

const incrementRetryAndRequest = (
  instance: AxiosInstance,
  request: RetriableAxiosConfig
) => {
  request._retryCount = (request._retryCount ?? 0) + 1;
  return instance.request(request);
};

const ensureAxiosHeaders = (headers?: RetriableAxiosConfig['headers']) =>
  headers instanceof AxiosHeaders ? headers : new AxiosHeaders(headers ?? {});

const getSessionId = () => {
  const existingSessionId =
    typeof window !== 'undefined'
      ? window.localStorage.getItem(sessionIdStorageKey)
      : null;

  if (existingSessionId) {
    return existingSessionId;
  }

  const newSessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `sid_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(sessionIdStorageKey, newSessionId);
  }

  return newSessionId;
};

export const resetSessionId = () => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(sessionIdStorageKey);
  }
};

const resolveCaptchaToken = async () => {
  if (!captchaPromise) {
    captchaPromise = showCaptcha()
      .catch((err) => {
        console.error('cannot resolve captcha token', err);
        throw err;
      })
      .finally(() => {
        captchaPromise = null;
      });
  }

  return captchaPromise;
};

const refreshAccessToken = async (
  instance: AxiosInstance,
  dispatch: AppDispatch
) => {
  if (!refreshPromise) {
    refreshPromise = instance
      .post(URL.REFRESH, {
        token: getToken('accessToken'),
      })
      .then(({ data }) => {
        const { user, accessToken, refreshToken } = data.data;
        saveTokens({ refreshToken, token: accessToken });
        saveAuthStage('authenticated');

        dispatch(updateAuthUser(user));

        return accessToken;
      })
      .catch((err) => {
        console.error("can't refresh token:", parseAxiosError(err));
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const handleResponseError = async (
  dispatch: AppDispatch,
  instance: AxiosInstance,
  error: AxiosError
) => {
  if (error && error.response) {
    const { config, response } = error;
    const { status } = response;

    switch (status) {
      case 401: {
        const originalRequest = config as RetriableAxiosConfig | undefined;
        const retryCount = originalRequest?._retryCount ?? 0;
        const hasRefreshToken = !!getToken('refreshToken');
        const isMfaAuthStage = getAuthStage() === 'mfa';
        const skipTokenRefresh =
          config?.url !== undefined && skipRefreshUrls.includes(config.url);
        const reachedRetryLimit = retryCount >= maxRequestRetryAttempts;
        const needLogout =
          !hasRefreshToken ||
          isMfaAuthStage ||
          reachedRetryLimit ||
          (config?.url && logoutOn401Urls.includes(config.url));

        if (skipTokenRefresh) {
          break;
        } else if (needLogout) {
          dispatch(logout({ error }));
          return Promise.reject(error);
        } else {
          try {
            await refreshAccessToken(instance, dispatch);
          } catch (refreshError) {
            dispatch(logout({ error: refreshError as AxiosError }));
            return Promise.reject(refreshError);
          }

          if (originalRequest) {
            return incrementRetryAndRequest(instance, originalRequest);
          }

          const retriableConfig = config as RetriableAxiosConfig;
          return incrementRetryAndRequest(instance, retriableConfig);
        }
        break;
      }

      case 404:
        if (
          config?.method === 'get' &&
          (!config?.headers ||
            !(config.headers as Record<string, any>)['allow-not-found'])
        ) {
          console.error('not found from', config?.url);
          if (
            !localStorage.getItem('allow-404') &&
            typeof window !== 'undefined'
          ) {
            window.location.href = Routes.NotFound;
          }
        }
        break;

      case 405: {
        if (
          Config.waf.enabled &&
          error.response &&
          error.response.status === 405 &&
          error.response.headers['x-amzn-waf-action'] === 'captcha'
        ) {
          const originalRequest = config as RetriableAxiosConfig | undefined;
          if (!originalRequest) {
            break;
          }

          let wafToken: string;
          try {
            wafToken = await resolveCaptchaToken();
          } catch (captchaError) {
            return Promise.reject(captchaError);
          }

          const headers = ensureAxiosHeaders(originalRequest.headers);
          headers.set(wafTokenHeaderKey, wafToken);
          originalRequest.headers = headers;

          return incrementRetryAndRequest(instance, originalRequest);
        }

        break;
      }
    }
  }
  return Promise.reject(error);
};

const getConfigString = (config?: AxiosRequestConfig) =>
  `${dayjs().format(
    'HH:mm:ss.SSS'
  )} | ${config?.method?.toUpperCase()}: ${config?.url}`;

const reportStart = (config: AxiosRequestConfig) => {
  console.log('started', getConfigString(config));
};

const reportEnd = (response: AxiosResponse) => {
  console.log('finished', getConfigString(response.config));
};

const reportFailed = (error: any) => {
  console.error('finished with error', getConfigString(error.config));
};

const showCaptcha = async () =>
  new Promise<string>((resolve, reject) => {
    const modalOverlay = document.getElementById('modalOverlay');
    const captcha = document.getElementById('captchaForm');
    if (!modalOverlay || !captcha) {
      reject(new Error('Container not found'));
      return;
    }

    modalOverlay.style.display = 'flex';

    window.AwsWafCaptcha.renderCaptcha(captcha, {
      apiKey: Config.waf.apiKey,
      onError: (err) => {
        modalOverlay.style.display = 'none';
        reject(err);
      },
      onSuccess: (token) => {
        modalOverlay.style.display = 'none';
        resolve(token);
      },
    });
  });

apiInstance.interceptors.request.use((config) => {
  reportStart(config);
  return configRequest(config);
});

export const interceptor = (dispatch: AppDispatch) => {
  apiLoaderInstance.interceptors.request.use(
    (config) => {
      reportStart(config);
      dispatch(setLoading(true));
      return configRequest(config);
    },
    (error) => {
      dispatch(setLoading(false));
      return error;
    }
  );

  apiLoaderInstance.interceptors.response.use(
    (response) => {
      dispatch(setLoading(false));
      reportEnd(response);
      return response;
    },
    (error) => {
      reportFailed(error);
      dispatch(setLoading(false));
      return handleResponseError(dispatch, apiLoaderInstance, error);
    }
  );

  apiInstance.interceptors.response.use(
    (response) => {
      reportEnd(response);
      return response;
    },
    (error) => {
      reportFailed(error);
      return handleResponseError(dispatch, apiInstance, error);
    }
  );
};
